// The adventure layer's TS view of `fk_forge::AdventureBlob` (05 §6.4, 11 §3.1 C2). The blob on
// `adventure_instance.blob` is postcard; `fk-sim-wasm.decode_adventure` turns it into the serde
// shape below (snake_case fields, unit enum variants as strings, payload variants as
// `{Variant: payload}`), and this file is the only place that knows that shape. The site-graph
// rules here (open neighbours) mirror `SiteGraph::open_neighbours` so the LocalMap can show which
// nodes the lead may click before the reducer confirms.
import { variant } from '../sim/adapt';
import { briefFoeWords } from '../story/lexicon';
import type { BattleBriefView } from '../story/genesis';

export type SiteKind = 'Gate' | 'Explore' | 'Clue' | 'Challenge' | 'Escalation' | 'Sanctum' | 'Stop' | 'Boss';
export type BeatKind = 'Briefing' | 'Explore' | 'Clue' | 'Puzzle' | 'Fight' | 'Settlement' | 'Crawl' | 'Exploration' | 'Boss';
/** docs/113: where a quest takes place (fk-schema `PuzzleGround`): the path's map and every encounter on it stand there. */
export type Scene = 'Keep' | 'Cistern' | 'Crypt' | 'Sewer' | 'Cellar' | 'Shrine' | 'Tomb' | 'Cavern' | 'Temple' | 'Bog' | 'Greenwood' | 'Autumnwood' | 'Pinewood' | 'Snowwood' | 'Desert' | 'Moor' | 'Jungle' | 'Underdark' | 'Ashlands';

export interface Site { id: number; name: string; kind: SiteKind; beat: BeatKind; fact?: number; x: number; y: number; minutes: number }
export interface Edge { a: number; b: number; lockedBy?: number }
export interface SiteGraph { sites: Site[]; edges: Edge[]; start: number; challenge: number; escalation: number; sanctum: number }
export interface OutcomeCell { row: OutcomeRowName; xp: number; gold: number; lootDraws: number; sentence: string; conditionStep: number }
export interface Adventure {
  archetype: string; title: string; pitch: string; asker: string; location: string; locationIndex: number;
  siteGraph: SiteGraph; outcomes: OutcomeCell[]; parMinutes: number; difficultyBp: number; side: boolean; minHumans: number; maxHumans: number;
  /** docs/50 §6: the quest's own fight, when the storyteller wrote one (who leads it, in words). */
  battle?: { leader: string; title: string; foes: string };
  /** docs/113 QP6: where the quest takes place, once a party has taken it up. */
  scene?: Scene;
}

export type OutcomeRowName = 'Success' | 'PartialSuccess' | 'SoftFail' | 'Abandoned' | 'Wipe';
/** `settlement.outcome_row` / `adventure_instance.outcome_row` codes (fk-world `outcome_row_code`). */
export const OUTCOME_ROWS: OutcomeRowName[] = ['Success', 'PartialSuccess', 'SoftFail', 'Abandoned', 'Wipe'];
export const OUTCOME_LABELS: Record<OutcomeRowName, string> = { Success: 'Success', PartialSuccess: 'Partial success', SoftFail: 'Soft fail', Abandoned: 'Abandoned', Wipe: 'Wipe' };
export const outcomeLabel = (code: number | undefined | null): string => (code === undefined || code === null ? 'unsettled' : OUTCOME_LABELS[OUTCOME_ROWS[code] ?? 'SoftFail'] ?? `row ${code}`);

/** Instance states (fk-world `INST_*`). */
export const INST = { PARTY_FORMING: 0, LOADED: 1, IN_ENCOUNTER: 2, COMPLETED: 3, FAILED: 4 } as const;
export const INST_NAMES = ['forming', 'loaded', 'in a room', 'completed', 'failed'];
/** Offer states (fk-world `OFFER_*`). */
export const OFFER = { OPEN: 0, TAKEN: 1, EXPIRED: 2 } as const;

/** Node-map glyph and a reading label per site kind. */
export const SITE_KIND: Record<SiteKind, { glyph: string; label: string }> = {
  Gate: { glyph: '⌂', label: 'gate' },
  Explore: { glyph: '◇', label: 'explore' },
  Clue: { glyph: '?', label: 'clue' },
  Challenge: { glyph: '⚙', label: 'challenge room' },
  Escalation: { glyph: '⚔', label: 'fight' },
  Sanctum: { glyph: '★', label: 'sanctum' },
  Stop: { glyph: '◆', label: 'stop' },
  Boss: { glyph: '☠', label: 'boss' },
};

/** docs/113: what a stop on a path holds, by its beat. */
export type StopKind = 'fight' | 'puzzle' | 'crawl' | 'explore' | 'boss' | 'gate' | 'reward';
export function stopKind(s: Site): StopKind {
  if (s.kind === 'Boss' || s.beat === 'Boss') return 'boss';
  if (s.kind === 'Gate') return 'gate';
  if (s.beat === 'Settlement') return 'reward';
  if (s.beat === 'Puzzle') return 'puzzle';
  if (s.beat === 'Crawl') return 'crawl';
  if (s.beat === 'Exploration' || s.beat === 'Explore') return 'explore';
  return 'fight';
}
/** A stop kind as the map names it, and its Oryx icon. */
export const STOP: Record<StopKind, { label: string; icon: string; blurb: string }> = {
  fight: { label: 'Fight', icon: 'item_sword', blurb: 'A fight on the way: a band of the foe’s people.' },
  puzzle: { label: 'Puzzle', icon: 'item_key_gold', blurb: 'A puzzle room: wits, not blades, open the way on.' },
  crawl: { label: 'Dungeon crawl', icon: 'stairs_down', blurb: 'A dungeon crawl: down through the dark to whatever dwells at the bottom.' },
  explore: { label: 'Exploration', icon: 'world_0453', blurb: 'An exploration: strange ground, wonders and hidden ways.' },
  boss: { label: 'Boss', icon: 'world_0556', blurb: 'The quest’s own foe waits here.' },
  gate: { label: 'Start', icon: 'gate_open', blurb: 'Where the party came in.' },
  reward: { label: 'Reward', icon: 'chest_gold_closed', blurb: 'The quest’s reward: walk here once the boss is beaten.' },
};

/** docs/113: the sites a party on a path can still reach from where it stands (the other branch of a choice it passed is lost). */
export function stillAhead(g: SiteGraph, current: number): Set<number> {
  const seen = new Set<number>([current]);
  const stack = [current];
  while (stack.length) {
    const x = stack.pop()!;
    for (const e of g.edges) if (e.a === x && !seen.has(e.b)) { seen.add(e.b); stack.push(e.b); }
  }
  return seen;
}

/** `location_status.condition` −3…+3 as a word (11 §10.1 location card). */
export function conditionWord(c: number): string {
  if (c <= -2) return 'Ruined';
  if (c < 0) return 'Troubled';
  if (c === 0) return 'Steady';
  return 'Thriving';
}

type Obj = Record<string, unknown>;
const num = (v: unknown): number => (typeof v === 'bigint' ? Number(v) : typeof v === 'number' ? v : Number(v ?? 0));
const payload = (v: unknown): Obj | undefined => (v && typeof v === 'object' ? (Object.values(v as Obj)[0] as Obj | undefined) : undefined);

function siteFromWasm(s: Obj): Site {
  const beat = s['beat'];
  const kind = variant(beat) as BeatKind;
  const site: Site = { id: num(s['id']), name: String(s['name']), kind: variant(s['kind']) as SiteKind, beat: kind, x: num(s['x']), y: num(s['y']), minutes: num(s['minutes']) };
  if (kind === 'Clue') site.fact = num(payload(beat)?.['fact']);
  return site;
}

export function adventureFromWasm(a: Obj): Adventure {
  const g = a['site_graph'] as Obj;
  const rows = ((a['outcome_table'] as Obj)['rows'] as Array<[unknown, Obj]>) ?? [];
  return {
    archetype: variant(a['archetype']), title: String(a['title']), pitch: String(a['pitch']), asker: String(a['asker']), location: String(a['location']), locationIndex: num(a['location_index']),
    siteGraph: {
      sites: ((g['sites'] as Obj[]) ?? []).map(siteFromWasm),
      edges: ((g['edges'] as Obj[]) ?? []).map((e): Edge => { const out: Edge = { a: num(e['a']), b: num(e['b']) }; if (e['locked_by'] !== null && e['locked_by'] !== undefined) out.lockedBy = num(e['locked_by']); return out; }),
      start: num(g['start']), challenge: num(g['challenge']), escalation: num(g['escalation']), sanctum: num(g['sanctum']),
    },
    outcomes: rows.map(([row, cell]) => { const r = cell['rewards'] as Obj; return { row: variant(row) as OutcomeRowName, xp: num(r['xp']), gold: num(r['gold']), lootDraws: num(r['loot_draws']), sentence: String(cell['chronicle_sentence']), conditionStep: num(cell['condition_step']) }; }),
    parMinutes: num(a['par_minutes']), difficultyBp: num(a['difficulty_bp']), side: Boolean(a['side']), minHumans: num(a['min_humans']), maxHumans: num(a['max_humans']),
    ...battleFromWasm(a['battle']),
    ...(typeof a['scene'] === 'string' ? { scene: a['scene'] as Scene } : {}),
  };
}

function battleFromWasm(b: unknown): Pick<Adventure, 'battle'> {
  if (!b || typeof b !== 'object') return {};
  const o = b as Obj;
  const leader = (o['leader'] as Obj | undefined) ?? {};
  const brief = o['brief'] as BattleBriefView | undefined;
  return { battle: { leader: String(leader['name'] ?? ''), title: String(leader['title'] ?? ''), foes: brief ? briefFoeWords(brief) : '' } };
}

/** docs/113: a quest's path (walked one way, stop by stop), not a site map (mirrors `SiteGraph::is_path`). */
export const isPath = (g: SiteGraph): boolean => g.sites.some((s) => s.kind === 'Stop' || s.kind === 'Boss');

/** Sites adjacent to `from` whose edge is open given the cleared set (mirrors `SiteGraph::open_neighbours`; a path's edges run one way). */
export function openNeighbours(g: SiteGraph, from: number, cleared: readonly number[]): number[] {
  const out = new Set<number>();
  const path = isPath(g);
  for (const e of g.edges) {
    if (e.a !== from && (path || e.b !== from)) continue;
    if (e.lockedBy !== undefined && !cleared.includes(e.lockedBy)) continue;
    out.add(e.a === from ? e.b : e.a);
  }
  return [...out].sort((x, y) => x - y);
}

export const edgeOpen = (e: Edge, cleared: readonly number[]): boolean => e.lockedBy === undefined || cleared.includes(e.lockedBy);
export const siteById = (g: SiteGraph, id: number): Site | undefined => g.sites.find((s) => s.id === id);

/** What travelling to a site does, for the node's hover/aria text (05 §6.4 beats). */
export function beatText(s: Site, cleared: readonly number[], battle?: Adventure['battle']): string {
  // docs/50 §6: the quest's own foe waits at its fight
  if (s.beat === 'Fight' && battle?.leader && !cleared.includes(s.id)) return `the escalation — ${battle.foes ? `${battle.foes.charAt(0).toLowerCase()}${battle.foes.slice(1)} under ` : ''}${battle.leader}, ${battle.title}`;
  switch (s.beat) {
    case 'Crawl': return cleared.includes(s.id) ? 'the crawl, cleared' : 'a dungeon crawl';
    case 'Exploration': return cleared.includes(s.id) ? 'explored' : 'an exploration';
    case 'Boss': return cleared.includes(s.id) ? 'the boss, beaten' : 'the boss';
    case 'Briefing': return 'the gate — where the party set out';
    case 'Explore': return 'an exploration site';
    case 'Clue': return 'a clue site — the party records what it finds';
    case 'Puzzle': return cleared.includes(s.id) ? 'the challenge room, cleared' : 'the challenge room — opens a puzzle encounter';
    case 'Fight': return cleared.includes(s.id) ? 'the fight, won' : 'the escalation — opens a fight';
    case 'Settlement': return 'the sanctum — settles the adventure';
  }
}
